import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import { useUserStore } from '../../lib/stores/userStore';
import { legacyToTiptap, tiptapToLegacy, extractMentionIds } from '../../lib/utils/mentionFormatConverter';
import { User, Bold, Italic, List, Paperclip } from 'lucide-react';

interface CommentEditorProps {
  value: string;
  onChange: (value: string, mentions: string[]) => void;
  placeholder?: string;
  className?: string;
  rows?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  onAttachClick?: () => void;
  attachmentCount?: number;
}

interface MentionUser {
  id: string;
  label: string;
  email: string;
  avatar?: string;
}

interface MentionListProps {
  items: MentionUser[];
  command: (item: MentionUser) => void;
}

// Mention suggestion dropdown component
const MentionList = React.forwardRef<HTMLDivElement, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  // Expose onKeyDown method for TipTap to call
  React.useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : props.items.length - 1
        );
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((prevIndex) =>
          prevIndex < props.items.length - 1 ? prevIndex + 1 : 0
        );
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        if (props.items[selectedIndex]) {
          props.command(props.items[selectedIndex]);
        }
        return true;
      }

      return false;
    },
  }));

  if (props.items.length === 0) {
    return (
      <div
        ref={ref}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-2"
      >
        <div className="text-sm text-gray-500 dark:text-gray-400">Nessun utente trovato</div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-w-[200px] max-w-[300px]"
    >
      <div className="py-1">
        {props.items.map((item, index) => (
          <div
            key={item.id}
            className={`flex items-center space-x-2 px-3 py-2 cursor-pointer transition-colors ${
              index === selectedIndex
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            onClick={() => props.command(item)}
          >
            <div className="flex-shrink-0">
              {item.avatar ? (
                <img
                  src={item.avatar}
                  alt={item.label}
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <div className="h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <User className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {item.label}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {item.email}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-1">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          ↑↓ per navigare • Invio per selezionare • Esc per annullare
        </div>
      </div>
    </div>
  );
});

MentionList.displayName = 'MentionList';

export function CommentEditor({
  value,
  onChange,
  placeholder = 'Scrivi un commento...',
  className = '',
  rows = 2,
  disabled = false,
  autoFocus = false,
  onAttachClick,
  attachmentCount = 0,
}: CommentEditorProps) {
  const isInitialMount = useRef(true);

  // Calculate min-height based on rows prop (simulate textarea rows)
  const minHeight = rows * 18; // 18px per row (ridotto da 20px)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable some features for simple comment input
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        // Enable lists for comments
        bulletList: {
          HTMLAttributes: {
            class: 'list-disc list-inside',
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: 'list-decimal list-inside',
          },
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Mention.configure({
        HTMLAttributes: {
          class: 'mention bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded px-1',
        },
        renderLabel({ node }) {
          return `@${node.attrs.label}`;
        },
        suggestion: {
          char: '@',
          items: async ({ query }) => {
            // Garantisci che gli utenti siano caricati
            const store = useUserStore.getState();
            if (store.users.length === 0) {
              await store.fetchUsers();
            }
            const currentUsers = useUserStore.getState().users;
            return currentUsers
              .filter((user) => {
                const searchText = query.toLowerCase();
                const name = (user.full_name || user.email).toLowerCase();
                const email = user.email.toLowerCase();
                return name.includes(searchText) || email.includes(searchText);
              })
              .slice(0, 5)
              .map((user) => ({
                id: user.id,
                label: user.full_name || user.email.split('@')[0],
                email: user.email,
                avatar: user.avatar_url,
              }));
          },
          render: () => {
            let component: ReactRenderer<MentionListProps> | null = null;
            let popup: TippyInstance[] | null = null;

            return {
              onStart: (props) => {
                component = new ReactRenderer(MentionList, {
                  props: {
                    items: props.items as MentionUser[],
                    command: props.command,
                  },
                  editor: props.editor,
                });

                if (!props.clientRect) {
                  return;
                }

                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect as () => DOMRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                  theme: 'light-border',
                  animation: 'shift-away',
                  inertia: true,
                  duration: [200, 150],
                  zIndex: 10100,
                });
              },
              onUpdate(props) {
                component?.updateProps({
                  items: props.items as MentionUser[],
                  command: props.command,
                });

                if (!props.clientRect) {
                  return;
                }

                popup?.[0]?.setProps({
                  getReferenceClientRect: props.clientRect as () => DOMRect,
                });
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup?.[0]?.hide();
                  return true;
                }
                // Delegate to component's onKeyDown
                return component?.ref?.onKeyDown?.(props) ?? false;
              },
              onExit() {
                popup?.[0]?.destroy();
                component?.destroy();
              },
            };
          },
        },
      }),
    ],
    content: legacyToTiptap(value),
    editable: !disabled,
    autofocus: autoFocus,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const legacy = tiptapToLegacy(html);
      const mentions = extractMentionIds(legacy);
      onChange(legacy, mentions);
    },
  });

  // Update editor content when value prop changes (but not on initial mount)
  useEffect(() => {
    if (!editor || isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const currentHtml = editor.getHTML();
    const currentLegacy = tiptapToLegacy(currentHtml);

    // Only update if value has changed to avoid cursor issues
    if (currentLegacy !== value) {
      const newHtml = legacyToTiptap(value);
      editor.commands.setContent(newHtml);
    }
  }, [value, editor]);

  // Update editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`comment-editor-wrapper ${className}`}>
      {/* Formatting Toolbar - minimale e integrato */}
      {!disabled && (
        <div className="flex items-center space-x-0.5 px-1 py-1">
          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBold().run()}
            className={`p-1 rounded transition-colors ${
              editor?.isActive('bold')
                ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#37352F] dark:text-gray-100'
                : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50'
            }`}
            title="Grassetto (Ctrl+B)"
          >
            <Bold className="h-3 w-3" />
          </button>

          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            className={`p-1 rounded transition-colors ${
              editor?.isActive('italic')
                ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#37352F] dark:text-gray-100'
                : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50'
            }`}
            title="Corsivo (Ctrl+I)"
          >
            <Italic className="h-3 w-3" />
          </button>

          <button
            type="button"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            className={`p-1 rounded transition-colors ${
              editor?.isActive('bulletList')
                ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#37352F] dark:text-gray-100'
                : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50'
            }`}
            title="Elenco puntato"
          >
            <List className="h-3 w-3" />
          </button>

          {onAttachClick && (
            <button
              type="button"
              onClick={onAttachClick}
              className={`p-1 rounded transition-colors relative ${
                attachmentCount > 0
                  ? 'bg-[rgba(55,53,47,0.08)] dark:bg-gray-700 text-[#37352F] dark:text-gray-100'
                  : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50'
              }`}
              title="Allega file"
            >
              <Paperclip className="h-3 w-3" />
              {attachmentCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#0f6cbd] text-white text-[9px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-medium">
                  {attachmentCount}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      <div
        className="comment-editor"
        style={{
          minHeight: `${minHeight}px`,
          maxHeight: '200px',
          overflowY: 'auto',
        }}
      >
        <EditorContent
          editor={editor}
          className={`
            prose prose-sm max-w-none
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        />
      </div>

      <style jsx global>{`
        .comment-editor .ProseMirror {
          outline: none;
          min-height: ${minHeight}px;
          padding: 8px 12px;
        }

        .comment-editor .ProseMirror p {
          margin: 0;
          line-height: 1.5;
        }

        .comment-editor .ProseMirror ul,
        .comment-editor .ProseMirror ol {
          margin: 4px 0;
          padding-left: 20px;
        }

        .comment-editor .ProseMirror li {
          margin: 2px 0;
        }

        .comment-editor .ProseMirror li > p {
          margin: 0;
        }

        .comment-editor .ProseMirror p.is-editor-empty:first-child::before {
          color: #9CA3AF;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }

        .dark .comment-editor .ProseMirror p.is-editor-empty:first-child::before {
          color: #6B7280;
        }

        .comment-editor .mention {
          background-color: #DBEAFE;
          border-radius: 0.25rem;
          padding: 0 0.25rem;
          color: #1D4ED8;
        }

        .dark .comment-editor .mention {
          background-color: rgba(37, 99, 235, 0.2);
          color: #93C5FD;
        }

        /* Remove tippy backdrop */
        .tippy-box[data-theme~='light-border'] {
          background-color: transparent;
          box-shadow: none;
        }

        .tippy-box[data-theme~='light-border'] .tippy-content {
          padding: 0;
        }

        /* Hide tippy backdrop/overlay */
        .tippy-backdrop {
          display: none !important;
        }
      `}</style>
    </div>
  );
}
